import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { Suspense, useEffect, useState, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Pressable } from "react-native";
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
  RelayEnvironmentProvider,
  useRelayEnvironment,
} from "react-relay";
import * as yup from "yup";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Input } from "@whoosh/design-system/src/components/controls/Input";
import { Loader } from "@whoosh/design-system/src/components/display";
import { TrashIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box, Text } from "@whoosh/design-system/src/components/primitive";

import {
  LanguageSelectContainer,
  usePatronLanguageSelectorEnabled,
} from "../../../../../features/LanguagePreference";
import { GoBackToPreviousStepAtom } from "../../../../../features/ProfileUpdateForm/components/UpdateEmailOrPhoneProfile/atoms/actions";
import { UpdateEmailOrPhoneProfile } from "../../../../../features/ProfileUpdateForm/components/UpdateEmailOrPhoneProfile/components/UpdateEmailOrPhoneProfile";
import { BiometricUnlockSetting } from "../BiometricUnlockSetting";
import { useUpdateMemberUser } from "../hooks/useUpdateMemberUser";
import { AccountModalQuery } from "./__generated__/AccountModalQuery.graphql";

type AccountModalProps = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  setIsDeleteAccountModalOpen: (val: boolean) => void;
};

export const AccountModal = ({
  isOpen,
  setIsOpen,
  setIsDeleteAccountModalOpen,
}: AccountModalProps) => {
  const [queryRef, loadQuery] = useQueryLoader<AccountModalQuery>(
    AccountModalQueryString
  );

  useEffect(() => {
    loadQuery({});
  }, [loadQuery]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <AccountModalInner
          queryRef={queryRef}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          setIsDeleteAccountModalOpen={setIsDeleteAccountModalOpen}
        />
      ) : null}
    </Suspense>
  );
};

const schema = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().nullable(),
  email: yup.string().email().nullable(),
  phoneNumber: yup.string().nullable(),
});

type AccountModalInnerProps = {
  queryRef: PreloadedQuery<AccountModalQuery>;
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  setIsDeleteAccountModalOpen: (val: boolean) => void;
};

type AccountForm = {
  firstName: string;
  lastName: string | null;
  email: string | null;
  phoneNumber: string;
};

const AccountModalInner = ({
  queryRef,
  isOpen,
  setIsOpen,
  setIsDeleteAccountModalOpen,
}: AccountModalInnerProps) => {
  const data = usePreloadedQuery(AccountModalQueryString, queryRef);
  const goBackToInitialStep = useSetAtom(GoBackToPreviousStepAtom);
  const languageSelectorEnabled = usePatronLanguageSelectorEnabled();
  const user = data.session?.user;

  const defaultValues = useMemo(() => {
    return {
      firstName: user?.firstName || "",
      lastName: user?.lastName,
      email: user?.email,
      phoneNumber:
        user?.phoneNumber?.nationalNumber ||
        (user?.memberProfiles?.edges &&
          user?.memberProfiles?.edges[0]?.node?.member?.phoneNumber
            ?.nationalNumber) ||
        "",
    };
  }, [user]);

  const defaultCountryCodeId =
    data.session?.currentClientProfile?.member?.club?.defaultCountryCode ||
    "US";

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<AccountForm>({
    resolver: yupResolver(schema),
    mode: "onBlur",
    defaultValues,
  });

  const { showToast } = useToast();

  const onUpdateCallback = () => {
    setIsOpen(false);
    showToast({
      description: t`Your Whoosh account has been updated successfully`,
      semanticStyle: "success",
      toastId: "whoosh-account-update",
    });
  };

  const [updateEmailOrPhone, setUpdateEmailOrPhone] = useState<
    "phone" | "email" | null
  >(null);

  const [updateMemberUser, isUpdatingMemberUser] =
    useUpdateMemberUser(onUpdateCallback);

  const onSubmit = ({ firstName, lastName }: AccountForm) => {
    updateMemberUser({ firstName, lastName });
  };

  const relayEnvironment = useRelayEnvironment();

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [defaultValues, isOpen, reset]);

  return (
    <WithRealSafeAreaInsets>
      <StandardModal
        upperTitle={updateEmailOrPhone ? undefined : t`Account`}
        isOpen={isOpen}
        hideFooterPadding
        hideCloseButton={updateEmailOrPhone ? true : false}
        onClose={() => {
          setIsOpen(false);
          setUpdateEmailOrPhone(null);
          goBackToInitialStep();
        }}
        content={
          <>
            {updateEmailOrPhone ? (
              <RelayEnvironmentProvider environment={relayEnvironment}>
                <UpdateEmailOrPhoneProfile
                  defaultCountryCodeId={defaultCountryCodeId}
                  onClose={() => {
                    setUpdateEmailOrPhone(null);
                    setIsOpen(false);
                    goBackToInitialStep();
                  }}
                  onComplete={() => {
                    setUpdateEmailOrPhone(null);
                    setIsOpen(false);
                    goBackToInitialStep();
                  }}
                  updateEmailOrPhone={updateEmailOrPhone}
                />
              </RelayEnvironmentProvider>
            ) : (
              <Box flex={1} px={6}>
                {isUpdatingMemberUser ? <Loader /> : null}

                <Box mt={6} gap={4}>
                  <Box gap={2}>
                    <Text variant="mainSmallSemiBold">{t`First Name`}</Text>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => {
                        return (
                          <Input
                            testID="firstName"
                            name="firstName"
                            size="large"
                            placeholder={t`John`}
                            value={field.value}
                            onChange={(val) => field.onChange(val)}
                            hasError={!!errors.firstName}
                            subMessage={errors.firstName?.message ?? ""}
                          />
                        );
                      }}
                    />
                  </Box>
                  <Box gap={2}>
                    <Text variant="mainSmallSemiBold">{t`Last Name`}</Text>
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => {
                        return (
                          <Input
                            testID="lastName"
                            name="lastName"
                            size="large"
                            placeholder={t`Smith`}
                            value={field.value ?? ""}
                            onChange={(val) => field.onChange(val)}
                            hasError={!!errors.lastName}
                            subMessage={errors.lastName?.message ?? ""}
                          />
                        );
                      }}
                    />
                  </Box>
                  <Box gap={2}>
                    <Text variant="mainSmallSemiBold">{t`Email`}</Text>
                    <Controller
                      control={control}
                      name="email"
                      render={({ field }) => {
                        return (
                          <Input
                            testID="email"
                            name="email"
                            isDisabled
                            size="large"
                            placeholder={t`Email`}
                            value={field.value ?? ""}
                            onChange={(val) => field.onChange(val)}
                            hasError={!!errors.email}
                            subMessage={errors.email?.message ?? ""}
                          />
                        );
                      }}
                    />
                    <Box alignItems={"baseline"} marginTop={2}>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t`Change Email`}
                        onPress={() => setUpdateEmailOrPhone("email")}
                      >
                        <Box
                          padding={2}
                          borderRadius="2"
                          backgroundColor="grayscale-100"
                        >
                          <Text variant="mainTiny">{t`Change Email`}</Text>
                        </Box>
                      </Pressable>
                    </Box>
                  </Box>

                  <Box gap={2}>
                    <Text variant="mainSmallSemiBold">{t`Phone Number`}</Text>
                    <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field }) => {
                        return (
                          <Input
                            testID="phoneNumber"
                            name="phoneNumber"
                            size="large"
                            placeholder={t`Phone Number`}
                            value={field.value ?? ""}
                            onChange={(val) => field.onChange(val)}
                            hasError={!!errors.phoneNumber}
                            subMessage={errors.phoneNumber?.message ?? ""}
                            isDisabled
                          />
                        );
                      }}
                    />
                    <Box alignItems={"baseline"} marginTop={2}>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel={t`Change Phone Number`}
                        onPress={() => setUpdateEmailOrPhone("phone")}
                      >
                        <Box
                          padding={2}
                          borderRadius="2"
                          backgroundColor="grayscale-100"
                        >
                          <Text variant="mainTiny">{t`Change Phone Number`}</Text>
                        </Box>
                      </Pressable>
                    </Box>
                  </Box>
                  {languageSelectorEnabled ? (
                    <Box gap={2}>
                      <Text variant="mainSmallSemiBold">{t`Language`}</Text>
                      <Box alignItems="flex-start">
                        <LanguageSelectContainer
                          syncToUserProfile
                          testID="account-language-select"
                        />
                      </Box>
                    </Box>
                  ) : null}
                  <BiometricUnlockSetting userId={user?.id} />
                  <Box gap={2} mt={4}>
                    <Text variant="mainSmallSemiBold">{t`Delete Whoosh Account`}</Text>
                    <Button
                      variant="danger"
                      leftIcon={<TrashIcon color="status-error-500" />}
                      onPress={() => {
                        setIsOpen(false);
                        setIsDeleteAccountModalOpen(true);
                      }}
                      accessibilityLabel={t`Delete Account`}
                      testID="account-modal-delete-account-button"
                    >
                      {t`Delete Account`}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        }
        footerContent={
          updateEmailOrPhone ? undefined : (
            <ScreenFooter>
              <Button
                onPress={handleSubmit(onSubmit)}
                accessibilityLabel={t`Save Changes`}
                testID="account-modal-save-changes-button"
              >{t`Save Changes`}</Button>
            </ScreenFooter>
          )
        }
      />
    </WithRealSafeAreaInsets>
  );
};

const AccountModalQueryString = graphql`
  query AccountModalQuery {
    session {
      currentClientProfile {
        member {
          club {
            defaultCountryCode
          }
        }
      }
      user {
        id
        email
        firstName
        lastName
        ... on MemberUser {
          phoneNumber {
            nationalNumber
          }
          memberProfiles {
            edges {
              node {
                member {
                  ... on Member {
                    phoneNumber {
                      nationalNumber
                    }
                  }
                  ... on Customer {
                    phoneNumber {
                      nationalNumber
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
